import { useEffect, useMemo, useState } from "react";
import {
  questionsNeeded, readInventory, countdownText, firstDropWindowOpens,
  readableDate,
} from "../shared/season-plan";
import { describeWindow } from "../shared/copy";

// The host's view once the season is running.
//
// A season in flight asks a different question from a season being set up. Not
// "what do I choose" but "is this thing healthy, and is anyone waiting on me".
// So the loud part at the top is the two facts that answer it, and everything
// else is a calm card that stays shut until it is wanted.
//
// Content-blind throughout, and that holds for the host as much as anyone: who
// is set up and who to chase, counts of what is ready, never a word of what
// anybody wrote or a single secret drop time.

export type HostSection = "season" | "crew" | "drops" | "alerts";

interface Props {
  league: any;
  status: any;
  // Each card opens the section that answers it, so the summary and the
  // controls behind it are never more than one tap apart.
  onOpenSection: (s: HostSection) => void;
  // Only offered once the season is over, so it never competes with the
  // controls a running season needs.
  onSeeFinale?: () => void;
}

export function HostHub({ league, status, onOpenSection, onSeeFinale }: Props) {
  const season = status.season;

  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const members = (status.members ?? []).filter((m: any) => m.inviteStatus !== "removed");
  const joined = members.filter((m: any) => m.inviteStatus === "joined");
  const checkedIn = members.filter((m: any) => m.checkedIn).length;
  const approved = members.reduce((n: number, m: any) => n + (m.readyCount ?? 0), 0);
  const noDevice = joined.filter((m: any) => m.devices === 0).length;
  const counts = status.dropCounts ?? { scheduled: 0, live: 0, revealed: 0 };

  const needed = useMemo(() => questionsNeeded({
    startsOn: season?.starts_on ?? "", endsOn: season?.ends_on ?? "",
    activeWeekdays: season?.active_weekdays ?? [],
    dropsPerDay: season?.drops_per_day ?? 1,
    questionsPerDrop: season?.default_questions_per_drop ?? 2,
  }), [season]);
  const inventory = readInventory(needed, approved);

  // The next window we can honestly name. Secret drop moments never leave the
  // server, so this is the window, never the moment inside it.
  const nextOpensIso = useMemo(() => firstDropWindowOpens({
    startsOn: new Date(tick).toISOString().slice(0, 10) > (season?.starts_on ?? "")
      ? new Date(tick).toISOString().slice(0, 10)
      : season?.starts_on ?? "",
    endsOn: season?.ends_on ?? "",
    activeWeekdays: season?.active_weekdays ?? [],
    windowStart: season?.timing_style === "exact"
      ? ((season?.exact_times_local ?? [])[0] ?? season?.drop_window_start_local)
      : season?.drop_window_start_local,
    timezone: league?.timezone ?? "America/New_York",
  }), [season, league, tick]);

  const live = counts.live > 0;
  const finished = season?.status === "complete";
  const windowMinutes = season?.play_window_minutes ?? (season?.drop_open_hours ?? 12) * 60;

  return (
    <>
      {/* The status block. Loud on purpose: the old page buried "status: active"
          in lowercase body text, which is not how you tell someone their season
          is running. */}
      <div className={`status-block${live ? " live" : ""}`}>
        <div className="status-top">
          <span className="status-crew">{league.name}</span>
          <span className={`status-pill${live ? " live" : ""}`}>
            {live ? "DROP LIVE" : season?.status === "complete" ? "FINISHED" : "RUNNING"}
          </span>
        </div>
        <div className="status-facts">
          <div className="status-fact">
            <span className="status-num">{checkedIn}<span className="status-den">/{joined.length}</span></span>
            <span className="status-cap">checked in</span>
          </div>
          <div className="status-fact">
            {/* A finished season was still counting down to a window that will
                never open, which reads as the app not knowing it ended. */}
            <span className="status-num">
              {finished
                ? `${counts.revealed ?? 0}`
                : live
                ? "now"
                : nextOpensIso
                ? countdownText(new Date(nextOpensIso).getTime(), tick)
                : "none"}
            </span>
            <span className="status-cap">
              {finished ? "drops played" : live ? "a drop is open" : "until the next window"}
            </span>
          </div>
        </div>
      </div>

      {/* The one thing a host wants the moment their season ends, at the top
          where they are actually looking. */}
      {finished && onSeeFinale && (
        <button className="btn primary" onClick={onSeeFinale}>See the finale</button>
      )}

      {/* Running short only matters while there are drops left to feed. */}
      {!finished && inventory.verdict !== "good" && (
        <div className={`card inventory ${inventory.verdict}`}>
          <span className="tag">{inventory.headline}</span>
          <p className="muted">{inventory.detail}</p>
        </div>
      )}

      <HubCard
        title="Season"
        summary={`${readableDate(season?.starts_on)} to ${readableDate(season?.ends_on)} · ${season?.drops_per_day ?? 1} drop${(season?.drops_per_day ?? 1) === 1 ? "" : "s"} a day · ${describeWindow(windowMinutes)} to play`}
        onOpen={() => onOpenSection("season")}
      />

      <HubCard
        title="Crew"
        summary={`${joined.length} in${(status.pendingInvites ?? []).length > 0 ? `, ${(status.pendingInvites ?? []).length} invited` : ""} · ${approved} question${approved === 1 ? "" : "s"} ready`}
        onOpen={() => onOpenSection("crew")}
      />

      <HubCard
        title="Drops"
        summary={`${counts.revealed ?? 0} done · ${counts.scheduled ?? 0} still to come`}
        detail="Drop times stay secret, even from you. That is what keeps them a surprise for everybody, you included."
        onOpen={() => onOpenSection("drops")}
      />

      <HubCard
        title="Notifications"
        summary={noDevice === 0 ? "Everybody's phone is set up" : `${noDevice} ${noDevice === 1 ? "person has" : "people have"} no phone set up`}
        tone={noDevice === 0 ? "calm" : "attention"}
        onOpen={() => onOpenSection("alerts")}
      />

    </>
  );
}

function HubCard({
  title, summary, detail, onOpen, tone = "calm",
}: { title: string; summary: string; detail?: string; onOpen?: () => void; tone?: "calm" | "attention" }) {
  return (
    <div className={`card hub-card${tone === "attention" ? " attention" : ""}`}>
      <div className="hub-card-row">
        <div style={{ minWidth: 0 }}>
          <span className="tag">{title}</span>
          <p className="hub-summary">{summary}</p>
          {detail && <p className="muted" style={{ fontSize: 13 }}>{detail}</p>}
        </div>
        {onOpen && <button className="link" onClick={onOpen}>Open</button>}
      </div>
    </div>
  );
}
